import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import RadioGroup from '../../../../shared/components/forms/RadioGroup';
import { color_gradients_green_shift, color_shades_darker, color_shades_darkest, color_variants_shadow_20 } from '../../../../shared/constants/colors';
import { breakpoint_small } from '../../../../shared/constants/breakpoints';
import { css } from '@emotion/css';
import { style_border_default } from '../../../../shared/constants/stylesValues';
import RadioCardGroupWithAudio from '../../../../shared/components/forms/RadioCardGroupWithAudio';
import { SparkTelephonyDetailsFormData, SparkTelephonyDetailsProps } from './SparkTelephonyDetailsForm';
import { FormikProps } from 'formik';
import Button from '../../../../shared/components/general/Button';
import { voiceOptions } from '../../../../shared/constants/sparkConstants/voiceOptions';
import TextField from '../../../../shared/components/forms/TextField';
import TelephonyDeploymentRequest from '../../../../shared/models/telephonyDeployment/TelephonyDeploymentRequest';
import FullScreenLayout from '../../../../shared/scenes/fullScreenLayout';
import PageContainer from '../../../../shared/components/structure/PageContainer';
import ModalLayout from '../../../../shared/components/structure/ModalLayout';
import ModalHeader from '../../../../shared/components/general/ModalHeader';
import FooterBar from "../../../../shared/components/structure/FooterBar";
import CustomScrollbars from '../../../../shared/components/structure/CustomScrollbars';
import useWindowSize from '../../../../shared/hooks/UseWindowSize';
import UserAvatar from '../../../../shared/components/general/UserAvatar';
import MultiFileAudioPlayer from '../../../../shared/components/messagePreviews/MultiFileAudioPlayer';
import SelectField from '../../../../shared/components/forms/SelectField';
import { getLanguageName, getRegionName } from '../../../../shared/models/extensions/languages';

const maleIcon = require('../../../../shared/content/images/spark/male.svg');

const SparkTelephonyDetails: React.FC<SparkTelephonyDetailsProps & FormikProps<SparkTelephonyDetailsFormData>> = (props) => {

    const customVoiceOption = { value: "custom", label: "Custom", icon: maleIcon, description: "Custom voice", audioSrc: "", provider: "", voice: "" };

    const windowSize = useWindowSize();
    const MODAL_SCROLLBAR_SUBTRACTION: number = 200;

    const voiceOptionsWithCustom = [...voiceOptions, customVoiceOption];

    const [showAddVoiceModal, setShowAddVoiceModal] = useState(false);
    const [selectedVoiceModal, setSelectedVoiceModal] = useState(null);
    const [voiceToEdit, setVoiceToEdit] = useState(-1);
    const [selectedLocaleDeployment, setSelectedLocaleDeployment] = useState(props.selectedTelephonyDeployment);

    const hasMultipleLocales = props.allTelephonyDeployments && props.allTelephonyDeployments.length > 1;

    // map locale to display name
    const getLocaleDisplayName = (locale: string, deploymentName?: string) => {
        if (!locale) return locale;

        if (locale.length === 2) {
            const languageName = getLanguageName(locale) || locale;
            return deploymentName ? `${languageName} - ${deploymentName}` : languageName;
        } else if (locale.length === 5 && locale.includes('-')) {
            const [languageCode, regionCode] = locale.split('-');
            const languageName = getLanguageName(languageCode) || languageCode;
            const regionName = getRegionName(regionCode) || regionCode;
            const displayName = `${languageName} - ${regionName}`;
            return deploymentName ? `${displayName} - ${deploymentName}` : displayName;
        }

        //Fallback
        return deploymentName ? `${locale} - ${deploymentName}` : locale;
    };

    // This is going track which locale is being edited or right now selected
    const localeOptions = props.allTelephonyDeployments?.map(deployment => ({
        value: deployment.locale,
        label: getLocaleDisplayName(deployment.locale, deployment.name)
    })) || [];

    useEffect(() => {
        if (hasMultipleLocales && props.allTelephonyDeployments) {
            const currentDeployment = props.allTelephonyDeployments.find(
                deployment => deployment.locale === selectedLocaleDeployment.locale
            );
            if (currentDeployment) {
                setSelectedLocaleDeployment(currentDeployment);
                props.setFieldValue("ttsOptions", currentDeployment.ttsOptions || []);
                props.setFieldValue("callRecordingEnabled", currentDeployment.callRecordingEnabled);
                props.setFieldValue("performLanguageDetection", currentDeployment.performLanguageDetection);
                props.setFieldValue("dtmfTurnsToListen", currentDeployment.dtmfTurnsToListen);
                if (currentDeployment.virtualNumber) {
                    props.setFieldValue("virtualNumber", currentDeployment.virtualNumber);
                }
            }
        }
    }, [selectedLocaleDeployment.locale, hasMultipleLocales]);

    const mapTtsOptionsToVoices = (ttsOptions) => {
        if (!ttsOptions || !Array.isArray(ttsOptions)) {
            return [];
        }
        return ttsOptions.map(option => {
            const match = voiceOptions.find(
                fullOption =>
                    fullOption.provider?.toLowerCase() === option.ttsProvider?.toLowerCase() &&
                    fullOption.voice === option.ttsVoice
            );

            if (match) {
                return match;
            } else {
                return {
                    value: "custom",
                    label: "Custom",
                    icon: maleIcon,
                    description: "Custom voice",
                    audioSrc: "",
                    provider: option.ttsProvider,
                    voice: option.ttsVoice
                };
            }
        });
    };
    const [selectedVoices, setSelectedVoices] = useState(mapTtsOptionsToVoices(selectedLocaleDeployment.ttsOptions || []));

    const handleVoiceSelection = (e) => {
        const selectedVoiceRecord = { label: e.label, value: e.value, icon: e.icon, description: e.description, audioSrc: e.audioSrc, provider: e.provider, voice: e.voice }
        setSelectedVoiceModal(selectedVoiceRecord);
    }

    const handleLocaleChange = (selectedOption) => {
        if (props.allTelephonyDeployments) {
            const newDeployment = props.allTelephonyDeployments.find(
                deployment => deployment.locale === selectedOption.value
            );
            if (newDeployment) {
                setSelectedLocaleDeployment(newDeployment);
                setSelectedVoices(mapTtsOptionsToVoices(newDeployment.ttsOptions || []));
            }
        }
    };

    const setEditMode = (index: number) => {
        setVoiceToEdit(index);
        setShowAddVoiceModal(true);
    }

    const handleRemoveVoice = (index: number) => {
        const updated = [...props.values.ttsOptions];
        const updatedFull = [...selectedVoices];
        updated.splice(index, 1);
        updatedFull.splice(index, 1);
        props.setFieldValue("ttsOptions", updated);
        setSelectedVoices(updatedFull);
        setShowAddVoiceModal(false);
        setVoiceToEdit(-1);
    };

    const handleChangeVoice = (index: number) => {
        if (selectedVoiceModal != null) {
            const updatedVoices = [...selectedVoices];
            updatedVoices[index] = selectedVoiceModal;

            const updatedOptions = [...props.values.ttsOptions];
            updatedOptions[index] = { ttsProvider: selectedVoiceModal.provider, ttsVoice: selectedVoiceModal.voice }

            props.setFieldValue("ttsOptions", updatedOptions);
            setSelectedVoices(updatedVoices);
        }
        setSelectedVoiceModal(null);
        setShowAddVoiceModal(false);
        setVoiceToEdit(-1);
    }

    const handleSaveNewVoice = () => {
        if (selectedVoiceModal != null) {
            const updatedFull = [...selectedVoices, selectedVoiceModal];
            const updated = [...props.values.ttsOptions, { ttsProvider: selectedVoiceModal.provider, ttsVoice: selectedVoiceModal.voice }];
            props.setFieldValue("ttsOptions", updated);
            setSelectedVoices(updatedFull)
        }
        setSelectedVoiceModal(null);
        setShowAddVoiceModal(false);
    };

    const manualSubmit = () => {
        const telephonyDeploymentRequest: TelephonyDeploymentRequest = {
            noTracking: selectedLocaleDeployment.noTracking,
            phoneNumberStartsWith: selectedLocaleDeployment.phoneNumber[0] + selectedLocaleDeployment.phoneNumber[1] + selectedLocaleDeployment.phoneNumber[2],
            environmentId: selectedLocaleDeployment.environmentId,
            ttsOptions: props.values.ttsOptions,
            locale: selectedLocaleDeployment.locale,
            asrEndpointing: selectedLocaleDeployment.asrEndpointing,
            asrProvider: selectedLocaleDeployment.asrProvider,
            asrModel: selectedLocaleDeployment.asrModel,
            asrTier: selectedLocaleDeployment.asrTier,
            performLanguageDetection: props.values.performLanguageDetection ?? selectedLocaleDeployment.performLanguageDetection,
            dtmfTurnsToListen: props.values.dtmfTurnsToListen ?? selectedLocaleDeployment.dtmfTurnsToListen,
            ttsPronunciationsMap: selectedLocaleDeployment.ttsPronunciationsMap,
            firstMessageDelayMS: selectedLocaleDeployment.firstMessageDelayMS,
            mergeTurnsAfterPauses: selectedLocaleDeployment.mergeTurnsAfterPauses,
            mergeTurnTimeoutMS: selectedLocaleDeployment.mergeTurnTimeoutMS,
            disableBargeIn: selectedLocaleDeployment.disableBargeIn,
            repromptAttempts: selectedLocaleDeployment.repromptAttempts,
            repromptDelaySeconds: selectedLocaleDeployment.repromptDelaySeconds,
            name: selectedLocaleDeployment.name,
            proactiveResponseDelayMS: selectedLocaleDeployment.proactiveResponseDelayMS,
            proactiveResponses: selectedLocaleDeployment.proactiveResponses,
            useProactiveResponses: selectedLocaleDeployment.useProactiveResponses,
            maxProactiveResponseAttempts: selectedLocaleDeployment.maxProactiveResponseAttempts,
            followUpProactiveResponses: selectedLocaleDeployment.followUpProactiveResponses,
            followUpProactiveResponsesDelayMS: selectedLocaleDeployment.followUpProactiveResponsesDelayMS,
            disableWelcomeMessageBargeIn: selectedLocaleDeployment.disableWelcomeMessageBargeIn,
            disableSpeechStarted: selectedLocaleDeployment.disableSpeechStarted,
            speechStartedUnconfirmedBargeInLimit: selectedLocaleDeployment.speechStartedUnconfirmedBargeInLimit,
            speechStartedPauseMS: selectedLocaleDeployment.speechStartedPauseMS,
            speechStartedRequiredBufferCount: selectedLocaleDeployment.speechStartedRequiredBufferCount,
            callRecordingEnabled: props.values.callRecordingEnabled,
            speechStartedLevel: selectedLocaleDeployment.speechStartedLevel,
            attemptInitialTransfer: selectedLocaleDeployment.attemptInitialTransfer,
            initialTransferTimeoutSeconds: selectedLocaleDeployment.initialTransferTimeoutSeconds,
            initialTransferTarget: selectedLocaleDeployment.initialTransferTarget,
            initialTransferWithCallingNumber: selectedLocaleDeployment.initialTransferWithCallingNumber,
            postProactiveResponsePauseDelayMS: selectedLocaleDeployment.postProactiveResponsePauseDelayMS,
            virtualNumber: props.values.virtualNumber,
            shouldProcessAudio: selectedLocaleDeployment.shouldProcessAudio,
            phoneNumberCountryCode: selectedLocaleDeployment.phoneNumberCountryCode,
            disableSpamDetection: selectedLocaleDeployment.disableSpamDetection,
            immediateTransferHourCutoff: selectedLocaleDeployment.immediateTransferHourCutoff,
            aiReengageSecondsAfterImmediateTransfer: selectedLocaleDeployment.aiReengageSecondsAfterImmediateTransfer,
            spamDetectionPromptOverride: selectedLocaleDeployment.spamDetectionPromptOverride,
            spamDetectionTolerance: selectedLocaleDeployment.spamDetectionTolerance,
            spamDetectionTemperature: selectedLocaleDeployment.spamDetectionTemperature,
            spamDetectionGenAiProvider: selectedLocaleDeployment.spamDetectionGenAiProvider,
            tryWebsocketReconnectionOnWelcome: selectedLocaleDeployment.tryWebsocketReconnectionOnWelcome,
            platform: selectedLocaleDeployment.platform,
            useGenerativeProactiveResponse: selectedLocaleDeployment.useGenerativeProactiveResponse,
            generativeProactiveResponsePrompt: selectedLocaleDeployment.generativeProactiveResponsePrompt,
            lastProactiveResponsesToDisallow: selectedLocaleDeployment.lastProactiveResponsesToDisallow,
            notificationEmails: selectedLocaleDeployment.notificationEmails,
        }

        props.handleSubmitTelephonyForm(selectedLocaleDeployment.phoneNumber, telephonyDeploymentRequest);
    }
    return (
        <div>
            {hasMultipleLocales && (
                <>
                    <RadioGroupLabel>Select Locale</RadioGroupLabel>
                    <Description>Choose the locale configuration to edit. Each locale can have different voice settings.</Description>
                    <LocaleSelectWrapper>
                        <SelectField
                            useForm={false}
                            useFormTheme={true}
                            options={localeOptions}
                            value={selectedLocaleDeployment.locale}
                            onChange={handleLocaleChange}
                            placeholder="Select locale"
                            clearable={false}
                        />
                    </LocaleSelectWrapper>
                </>
            )}
            {selectedLocaleDeployment.virtualNumber &&
                <>
                    <RadioGroupLabel>Virtual Number</RadioGroupLabel>
                    <StyledTextField
                        name="virtualNumber"
                        value={props.values.virtualNumber}
                        onChange={(e) => props.setFieldValue("virtualNumber", e.target.value)}
                        placeholder="Update your virtual number here."
                        labelStyle={textFieldLabelStyle}
                    />
                </>
            }
            <RadioGroupLabel>Record Calls</RadioGroupLabel>
            <Description>Call recordings are available in session analytics.</Description>
            <RadioGroup
                checkContainerStyle={checkContainerStyle}
                selectedOptionStyle={selectedOptionStyle}
                onChange={(e) => props.setFieldValue("callRecordingEnabled", e.value)}
                value={props.values.callRecordingEnabled}
                options={[
                    { label: "Call Recording Enabled", value: true },
                    { label: "Call Recording Disabled", value: false },
                ]}
            />
            <RadioGroupLabel>Language Detection</RadioGroupLabel>
            <Description>Enable automatic language detection for incoming calls.</Description>
            <RadioGroup
                checkContainerStyle={checkContainerStyle}
                selectedOptionStyle={selectedOptionStyle}
                onChange={(e) => props.setFieldValue("performLanguageDetection", e.value)}
                value={props.values.performLanguageDetection}
                options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                ]}
            />
            <RadioGroupLabel>DTMF Turns to Listen</RadioGroupLabel>
            <Description>Number of turns to listen for DTMF (dial tone) input. Set to 0 to disable.</Description>
            <StyledTextField
                name="dtmfTurnsToListen"
                type="number"
                value={props.values.dtmfTurnsToListen}
                onChange={(e) => props.setFieldValue("dtmfTurnsToListen", parseInt(e.target.value) || 0)}
                placeholder="Enter number of turns to listen for DTMF"
                labelStyle={textFieldLabelStyle}
            />
            <RadioGroupLabel>Selected Voices</RadioGroupLabel>
            <Description>Here are the currently enabled voices. One will be randomly selected for each incoming call.</Description>
            <StyledVoiceTable>
                {selectedVoices?.map((voice, index) => (
                    <VoiceRow key={index}>
                        <LeftWrapper>
                            <AvatarWrapper>
                                <UserAvatar src={voice.icon} firstName='' lastName='' size={40} />
                            </AvatarWrapper>
                            <NameWrapper>
                                <StyledVoiceName>{voice.value}</StyledVoiceName>
                            </NameWrapper>
                        </LeftWrapper>
                        <RightWrapper>
                            <AudioWrapper>
                                {voice.value != "custom" && (
                                    <AudioPlayerWrapper>
                                        <MultiFileAudioPlayer src={[voice.audioSrc]} isLoading={false} onLoad={() => null} />
                                    </AudioPlayerWrapper>)}
                            </AudioWrapper>
                            <EditButton
                                themes={["secondary-small"]}
                                text="Edit Voice"
                                onClick={() => setEditMode(index)}
                            />
                        </RightWrapper>
                    </VoiceRow>
                ))}
            </StyledVoiceTable>
            <AddANewVoiceButton
                themes={["primary-small"]}
                text="+ Add a New Voice"
                onClick={() => setShowAddVoiceModal(true)}
            />
            {showAddVoiceModal && (
                <div>
                    <ModalLayout isVisible={true}>
                        <ModalHeader title='Preferred Voice' onClose={() => setShowAddVoiceModal(false)} />
                        <form>
                            <CustomScrollbars autoHide autoHeight autoHeightMax={(windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION}>
                                <VoiceCardsWrapper>
                                    <RadioCardGroupWithAudio
                                        onChange={(e) => handleVoiceSelection(e)}
                                        options={(selectedVoices?.some((option) => option.value === "custom")) ? voiceOptionsWithCustom : voiceOptions}
                                        value={selectedVoiceModal}
                                    />
                                </VoiceCardsWrapper>
                            </CustomScrollbars>
                        </form>
                        <FooterBar>
                            {voiceToEdit >= 0 && (
                                <div>
                                    <Button
                                        disabled={!showAddVoiceModal}
                                        themes={["primary"]}
                                        text="Update Voice"
                                        type="button"
                                        onClick={() => { handleChangeVoice(voiceToEdit); }}
                                    />
                                    {props.values.ttsOptions.length > 1 && selectedVoices.length > 1 && (
                                        <Button
                                            disabled={!showAddVoiceModal}
                                            themes={["secondary"]}
                                            text={`Remove ${selectedVoices[voiceToEdit]["value"]}`}
                                            type="button"
                                            onClick={() => handleRemoveVoice(voiceToEdit)}
                                        />)}
                                </div>
                            )}
                            {voiceToEdit < 0 && (
                                <Button
                                    disabled={!showAddVoiceModal}
                                    themes={["primary"]}
                                    text="Add Voice"
                                    type="button"
                                    onClick={() => { handleSaveNewVoice(); }}
                                />)}
                        </FooterBar>
                    </ModalLayout>
                </div>
            )}
            <Button
                disabled={!selectedLocaleDeployment}
                loading={props.isLoadingTelephonyDeployments}
                themes={["primary-small"]}
                text="Save"
                type="button"
                onClick={() => {
                    props.handleSubmit();
                    manualSubmit();
                }}
            />
        </div>
    );
};

const AddANewVoiceButton = styled(Button)`
    margin-left: 0;
    margin-right: 0;
`;

const RightWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const LeftWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const AvatarWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const AudioPlayerWrapper = styled.div`
    flex: 1;
    margin-top: 10px;
`;

const StyledVoiceTable = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 16px;
`;

const VoiceRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border: ${style_border_default};
    border-top: none;
    border-radius: 8px;
    height: 60px;
    &:first-of-type {
        border-top: ${style_border_default};
    }
`;

const NameWrapper = styled.div`
`;

const AudioWrapper = styled.div`
    width: 500px;
    ${breakpoint_small} {
        display: none;
    }
`;

const StyledVoiceName = styled.span`
    font-size: 16px;
    font-weight: 500;
    color: ${color_shades_darkest};
`;

const EditButton = styled(Button)``;

const textFieldLabelStyle = css`
    margin: 16px 0 0 0;
`;

const StyledTextField = styled(TextField)``;

const Description = styled.p`
    font-size: 14px;
    color: ${color_shades_darker};
    margin-top: 4px;
`;

const VoiceCardsWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;  
`;

const RadioGroupLabel = styled.p`
    font-weight: 400;
    font-size: 18px;
    color: ${color_shades_darkest};
    margin-top: 24px;
    &:first-of-type {
        margin-top: 0;
    }
`;

const checkContainerStyle = css`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #ffffff;
    border: ${style_border_default};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const selectedOptionStyle = css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-image: ${color_gradients_green_shift};
`;

const LocaleSelectWrapper = styled.div`
    margin-bottom: 24px;
    margin-top: -16px;
    max-width: 600px;
`;

export default SparkTelephonyDetails;

